import { Suspense, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon, type IconName } from '@/components/ui/Icon';
import { Logo } from '@/components/ui/Logo';
import { Avatar } from '@/components/ui/Avatar';
import { useAuth } from '@/stores/auth';
import { useChats, useConfig, useFriendRequests, useInvites, useLinkRequests } from '@/api/queries';
import { Skeleton } from '@/components/ui/Skeleton';
import { PlusBadge } from '@/components/ui/PlusBadge';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  active: string; // accent class when active
}

const NAV: NavItem[] = [
  { to: '/home', label: 'Home', icon: 'home', active: 'text-fg' },
  { to: '/requests', label: 'Requests', icon: 'heart', active: 'text-fg' },
  { to: '/friends', label: 'Friends', icon: 'people', active: 'text-fg' },
  { to: '/drops', label: 'Drops', icon: 'drop', active: 'text-fg' },
  { to: '/settings', label: 'You', icon: 'user', active: 'text-fg' },
];

function Badge({ n, testId, where = 'Friends' }: { n: number; testId?: string; where?: string }) {
  if (n <= 0) return null;
  return (
    <span
      className="absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-yellow px-1 text-[11px] font-bold text-night"
      aria-label={`${n} new in ${where}`}
      data-testid={testId}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

const testIdFor = (to: string) => (to === '/friends' ? 'friends-tab' : `nav-${to.slice(1)}`);

function RouteFallback() {
  return (
    <div className="mx-auto max-w-content px-4 pt-10 sm:px-6 lg:px-10" role="status" aria-label="Loading">
      <Skeleton className="mb-3 h-4 w-24" />
      <Skeleton className="mb-8 h-12 w-2/3" />
      <Skeleton className="h-64 w-full" rounded="rounded-card" />
    </div>
  );
}

export function AppShell() {
  const me = useAuth((s) => s.me);
  const invites = useInvites(!!me?.onboarded);
  const requests = useFriendRequests(!!me?.onboarded);
  const chats = useChats(!!me?.onboarded);
  const unread = (chats.data?.chats ?? []).reduce((n, c) => n + (c.unread_count > 0 ? c.unread_count : 0), 0);
  const pending = (invites.data?.incoming.length ?? 0) + (requests.data?.incoming.length ?? 0) + unread;
  // v12: the Requests tab (people who asked through your link) has its own badge.
  const config = useConfig();
  const linksOn = config.data?.features.date_links !== false && config.data?.features.date_invites !== false;
  const linkReqs = useLinkRequests(!!me?.onboarded && linksOn);
  const asked = linkReqs.data?.incoming.length ?? 0;
  const nav = NAV.filter((n) => n.to !== '/requests' || linksOn);
  const loc = useLocation();
  const desktop = useIsDesktop();
  const isMod = me?.role === 'moderator' || me?.role === 'admin';
  const inThread = /^\/friends\/[^/]+\/chat/.test(loc.pathname);
  const friendsActive = (p: string) => p.startsWith('/friends') || p.startsWith('/chats');

  return (
    <div className="min-h-[100dvh] lg:flex">
      {/* Desktop rail */}
      <aside className="sticky top-0 hidden h-[100dvh] w-[232px] shrink-0 flex-col bg-ground px-4 py-6 lg:flex" aria-label="Main">
        <NavLink to="/home" className="mb-10 px-2" aria-label="Popin home">
          <Logo />
        </NavLink>
        <nav className="flex flex-col gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={desktop ? testIdFor(n.to) : undefined}
              data-tour={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  'group relative flex h-12 items-center gap-3 rounded-full px-3 text-[15px] font-medium transition-colors',
                  isActive || (n.to === '/friends' && friendsActive(loc.pathname)) ? 'bg-surface font-semibold text-fg' : 'text-muted hover:bg-surface hover:text-fg',
                )
              }
            >
              <span className="relative">
                <Icon name={n.icon} />
                {n.to === '/friends' && <Badge n={pending} testId={desktop ? 'friends-badge' : undefined} />}
                {n.to === '/requests' && <Badge n={asked} where="Requests" testId={desktop ? 'requests-badge' : undefined} />}
              </span>
              <span>{n.label}</span>
            </NavLink>
          ))}
          {isMod && (
            <NavLink
              to="/mod"
              className={({ isActive }) =>
                cn(
                  'mt-4 flex h-11 items-center gap-3 rounded-full px-3 text-sm font-medium',
                  isActive || loc.pathname.startsWith('/mod') ? 'bg-raised text-fg' : 'text-muted hover:text-fg',
                )
              }
            >
              <Icon name="gavel" size={20} /> Moderation
            </NavLink>
          )}
        </nav>
        <div className="mt-auto flex items-center gap-3 rounded-card bg-surface p-3">
          <Avatar avatar={me?.avatar} size={36} />
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              <span className="truncate">{me?.display_name}</span>
              {me?.plan === 'plus' && <PlusBadge />}
            </p>
            <NavLink to="/safety" className="text-xs text-muted hover:text-fg">
              Safety Center
            </NavLink>
          </div>
        </div>
      </aside>

      <main id="main" className={cn('min-w-0 flex-1 lg:pb-0', !inThread && 'pb-[calc(76px+env(safe-area-inset-bottom))]')}>
        <motion.div key={loc.pathname.split('/')[1]} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15, ease: 'easeOut' }}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </motion.div>
      </main>

      {/* Mobile tab bar */}
      <nav
        aria-label="Main"
        className={cn('fixed inset-x-0 bottom-0 z-40 border-t border-line bg-ground pb-[env(safe-area-inset-bottom)] lg:hidden', inThread && 'hidden')}
      >
        <div className={cn('mx-auto grid max-w-lg', nav.length === 5 ? 'grid-cols-5' : 'grid-cols-4')}>
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={desktop ? undefined : testIdFor(n.to)}
              data-tour={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                cn('flex h-[64px] flex-col items-center justify-center gap-1 text-xs font-medium transition-colors', isActive || (n.to === '/friends' && friendsActive(loc.pathname)) ? 'font-semibold text-fg' : 'text-muted')
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon name={n.icon} strokeWidth={isActive ? 2.4 : 2} />
                    {n.to === '/friends' && <Badge n={pending} testId={desktop ? undefined : 'friends-badge'} />}
                    {n.to === '/requests' && <Badge n={asked} where="Requests" testId={desktop ? undefined : 'requests-badge'} />}
                  </span>
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={cn('mx-auto w-full px-4 pb-12 pt-6 sm:px-6 lg:px-10 lg:pt-10', wide ? 'max-w-[1280px]' : 'max-w-content', className)}>{children}</div>;
}

export function PageHeader({ kicker, title, actions, sub }: { kicker?: ReactNode; title: ReactNode; /** Ignored since v11 (no coloured kickers). */ accent?: string; actions?: ReactNode; sub?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4 lg:mb-8">
      <div>
        <h1 className="font-display text-[44px] leading-[0.92] sm:text-[56px]">{title}</h1>
        {(sub || kicker) && <p className="mt-2 max-w-xl text-[15px] text-muted">{sub ?? kicker}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </header>
  );
}
